import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  MessageCircle, Store, Copy, Check, Ticket, Calendar, Clock, MapPin,
  Briefcase, BedDouble, Newspaper, ChevronRight,
} from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { waLink, formatBRL, isExpired, couponTypeLabel } from "@/lib/site";
import { CategoryIcon } from "@/lib/iconLibrary";
import { SectionShell, Carousel } from "@/components/site/primitives";
import AutoCarousel from "@/components/site/AutoCarousel";
import { DOTS_DEFAULTS } from "@/lib/sectionVariants";

const useResource = (resource, params = "") => {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get(`/public/${resource}${params}`).then((r) => {
      if (alive) { setItems(r.data); setLoaded(true); }
    }).catch((e) => { if (alive) { console.error(`Erro ao carregar ${resource}`, e); setLoaded(true); } });
    return () => { alive = false; };
  }, [resource, params]);
  return { items, loaded };
};

const Img = ({ src, alt, className }) =>
  src ? (
    <img src={fileUrl(src)} alt={alt} className={className} loading="lazy" />
  ) : (
    <div className={`${className} bg-muted flex items-center justify-center`}>
      <Store className="w-8 h-8 text-muted-foreground/40" />
    </div>
  );

/* ---------------- Categories ---------------- */
export const CategoriesSection = ({ title = "Categorias", section }) => {
  const { items } = useResource("categories");
  if (!items.length) return null;
  return (
    <SectionShell title={title} id="categories">
      <AutoCarousel section={section || { key: "categories" }} testId="dots-categories">
        {items.map((c) => (
          <Link
            key={c.id}
            to={`/empresas?categoria=${encodeURIComponent(c.name)}`}
            data-testid={`category-${c.id}`}
            className="snap-item flex-none flex flex-col items-center gap-2 w-20"
          >
            <div className="w-16 h-16 rounded-2xl bg-card border border-border shadow-sm flex items-center justify-center overflow-hidden hover:border-primary hover:shadow-md transition-all">
              {c.image ? (
                <img src={fileUrl(c.image)} alt={c.name} className="w-full h-full object-cover" />
              ) : (
                <CategoryIcon name={c.icon} className="w-7 h-7 text-primary" />
              )}
            </div>
            <span className="text-xs font-medium text-center text-foreground leading-tight">{c.name}</span>
          </Link>
        ))}
      </AutoCarousel>
    </SectionShell>
  );
};

/* ---------------- Banners ---------------- */
export const BannersSection = ({ section }) => {
  const { items } = useResource("banners");
  const dots = { ...DOTS_DEFAULTS, ...(section?.dots || {}) };
  if (!items.length) return null;
  return (
    <section id="banners" className="py-3 sm:py-5" data-testid="section-banners">
      <div className="px-4 sm:px-6 max-w-6xl mx-auto">
        <AutoCarousel section={section || { key: "banners" }} dotsInside={dots.position === "inside"} testId="banner-dots">
          {items.map((b) => (
            <a
              key={b.id}
              href={b.link || "#"}
              data-testid={`banner-${b.id}`}
              className="snap-item flex-none w-[85%] sm:w-[48%] relative rounded-2xl overflow-hidden h-40 sm:h-48 group"
            >
              <Img src={b.image} alt={b.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute bottom-0 left-0 p-4 text-white">
                <h3 className="font-bold text-lg font-heading">{b.title}</h3>
                <p className="text-sm text-white/80 line-clamp-1">{b.text}</p>
                {b.button && (
                  <span className="inline-block mt-2 text-xs font-semibold bg-primary px-3 py-1.5 rounded-full">
                    {b.button}
                  </span>
                )}
              </div>
            </a>
          ))}
        </AutoCarousel>
      </div>
    </section>
  );
};

/* ---------------- Promotions ---------------- */
export const PromotionsSection = () => {
  const { items } = useResource("promotions");
  const visible = items.filter((p) => !isExpired(p.valid_until));
  if (!visible.length) return null;
  return (
    <SectionShell title="Promoções" id="promotions">
      <Carousel>
        {visible.map((p) => (
          <div key={p.id} data-testid={`promo-${p.id}`} className="snap-item flex-none w-[240px] bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
            <Img src={p.image} alt={p.title} className="w-full h-32 object-cover" />
            <div className="p-3">
              <h3 className="font-semibold text-sm text-foreground line-clamp-1">{p.title}</h3>
              <p className="text-xs text-muted-foreground line-clamp-2 mt-1 h-8">{p.description}</p>
              <div className="flex items-center gap-2 mt-2">
                {p.old_price ? <span className="text-xs text-muted-foreground line-through">{formatBRL(p.old_price)}</span> : null}
                <span className="text-lg font-bold text-primary font-heading">{formatBRL(p.new_price)}</span>
              </div>
              <Link to={p.business_id ? `/empresa/${p.business_id}` : "#"} className="mt-2 block text-center text-sm font-semibold text-primary border border-primary/30 rounded-full py-1.5 hover:bg-primary hover:text-primary-foreground transition-colors">
                Ver oferta
              </Link>
            </div>
          </div>
        ))}
      </Carousel>
    </SectionShell>
  );
};

/* ---------------- Coupons ---------------- */
export const CouponCard = ({ c, business }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(c.code);
      } else {
        const ta = document.createElement("textarea");
        ta.value = c.code;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
    } catch (e) {
      // ignore clipboard denials
    }
    setCopied(true);
    toast.success("Cupom copiado!");
    setTimeout(() => setCopied(false), 2000);
  };
  const wa = c.whatsapp || business?.whatsapp;
  return (
    <div data-testid={`coupon-${c.id}`} className="snap-item flex-none w-[250px] bg-orange-50 rounded-2xl border border-dashed border-primary/40 p-4 flex flex-col">
      <div className="flex items-center gap-2 mb-2">
        {c.image ? (
          <img src={fileUrl(c.image)} alt="" className="w-9 h-9 rounded-full object-cover border border-white" />
        ) : (
          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center"><Ticket className="w-4 h-4 text-primary" /></div>
        )}
        <span className="text-xs font-semibold text-muted-foreground line-clamp-1">{business?.name || "Empresa"}</span>
      </div>
      <div className="text-primary font-bold text-lg font-heading leading-tight">{couponTypeLabel(c.type, c.value)}</div>
      <p className="text-sm font-semibold text-foreground mt-0.5 line-clamp-1">{c.title}</p>
      <p className="text-xs text-muted-foreground line-clamp-2 mt-1 flex-1">{c.description}</p>
      <div className="flex items-center justify-between mt-2 bg-white rounded-lg px-3 py-2 border border-dashed border-primary/30">
        <span className="font-mono font-bold text-primary text-sm">{c.code}</span>
        <button onClick={copy} data-testid={`coupon-copy-${c.id}`} className="text-primary hover:opacity-70">
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
      {wa && (
        <a href={waLink(wa, `Olá! Vi o cupom ${c.code} no Divulga Pocinhos Guia e gostaria de utilizá-lo.`)} target="_blank" rel="noreferrer" data-testid={`coupon-wa-${c.id}`} className="mt-2 flex items-center justify-center gap-2 bg-secondary text-secondary-foreground text-sm font-semibold rounded-full py-2 active:scale-95 transition-transform">
          <MessageCircle className="w-4 h-4" /> Usar pelo WhatsApp
        </a>
      )}
    </div>
  );
};

export const CouponsSection = () => {
  const { items } = useResource("coupons");
  const [businesses, setBusinesses] = useState({});
  useEffect(() => {
    api.get("/public/businesses").then((r) => {
      const map = {};
      r.data.forEach((b) => (map[b.id] = b));
      setBusinesses(map);
    }).catch(() => {});
  }, []);
  const visible = items.filter((c) => !isExpired(c.valid_until));
  if (!visible.length) return null;
  return (
    <SectionShell title="Cupons de Desconto" id="coupons">
      <Carousel>
        {visible.map((c) => <CouponCard key={c.id} c={c} business={businesses[c.business_id]} />)}
      </Carousel>
    </SectionShell>
  );
};

/* ---------------- Business card ---------------- */
export const BusinessCard = ({ b }) => (
  <div data-testid={`business-card-${b.id}`} className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden flex flex-col">
    <div className="relative">
      <Img src={b.cover} alt={b.name} className="w-full h-32 object-cover" />
      <div className="absolute -bottom-6 left-3">
        <Img src={b.logo} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-white bg-white shadow" />
      </div>
    </div>
    <div className="pt-8 px-3 pb-3 flex flex-col flex-1">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-primary">{b.category}</span>
      <h3 className="font-bold text-foreground leading-tight font-heading line-clamp-1">{b.name}</h3>
      <p className="text-xs text-muted-foreground line-clamp-2 mt-1 flex-1">{b.description}</p>
      <div className="flex gap-2 mt-3">
        {b.whatsapp && (
          <a href={waLink(b.whatsapp, `Olá! Encontrei ${b.name} no Divulga Pocinhos Guia.`)} target="_blank" rel="noreferrer" data-testid={`business-wa-${b.id}`} className="flex-1 flex items-center justify-center gap-1 bg-secondary text-secondary-foreground text-xs font-semibold rounded-full py-2 active:scale-95 transition-transform">
            <MessageCircle className="w-4 h-4" /> WhatsApp
          </a>
        )}
        <Link to={`/empresa/${b.id}`} data-testid={`business-view-${b.id}`} className="flex-1 flex items-center justify-center gap-1 border border-border text-foreground text-xs font-semibold rounded-full py-2 hover:border-primary transition-colors">
          Ver empresa
        </Link>
      </div>
    </div>
  </div>
);

export const FeaturedBusinessesSection = () => {
  const { items } = useResource("businesses", "?featured=true");
  if (!items.length) return null;
  return (
    <SectionShell title="Empresas em Destaque" id="featured_businesses" viewAllTo="/empresas">
      <Carousel>
        {items.map((b) => (
          <div key={b.id} className="snap-item flex-none w-[270px]">
            <BusinessCard b={b} />
          </div>
        ))}
      </Carousel>
    </SectionShell>
  );
};

export const AllBusinessesSection = () => {
  const { items } = useResource("businesses");
  if (!items.length) return null;
  return (
    <SectionShell title="Todas as Empresas" id="all_businesses" viewAllTo="/empresas">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {items.slice(0, 8).map((b) => <BusinessCard key={b.id} b={b} />)}
      </div>
    </SectionShell>
  );
};

/* ---------------- News ---------------- */
export const NewsSection = () => {
  const { items } = useResource("news");
  if (!items.length) return null;
  return (
    <SectionShell title="Últimas Notícias" id="news">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {items.slice(0, 6).map((n) => (
          <Link key={n.id} to={`/noticia/${n.id}`} data-testid={`news-${n.id}`} className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden flex flex-col sm:flex-row lg:flex-col group">
            <Img src={n.image} alt={n.title} className="w-full sm:w-32 lg:w-full h-40 sm:h-auto lg:h-40 object-cover" />
            <div className="p-3 flex-1">
              <span className="text-[11px] font-semibold text-primary flex items-center gap-1"><Newspaper className="w-3 h-3" /> {n.category || "Notícia"} · {n.date}</span>
              <h3 className="font-bold text-foreground leading-tight font-heading line-clamp-2 mt-1 group-hover:text-primary transition-colors">{n.title}</h3>
              <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{n.summary}</p>
              <span className="text-sm font-semibold text-primary flex items-center gap-1 mt-2">Ler notícia <ChevronRight className="w-4 h-4" /></span>
            </div>
          </Link>
        ))}
      </div>
    </SectionShell>
  );
};

/* ---------------- Events ---------------- */
export const EventsSection = () => {
  const { items } = useResource("events");
  if (!items.length) return null;
  return (
    <SectionShell title="Eventos em Pocinhos" id="events">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {items.map((e) => (
          <Link key={e.id} to={`/evento/${e.id}`} data-testid={`event-${e.id}`} className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden flex group">
            <Img src={e.image} alt={e.name} className="w-28 h-full object-cover" />
            <div className="p-3 flex-1">
              <h3 className="font-bold text-foreground leading-tight font-heading line-clamp-1 group-hover:text-primary transition-colors">{e.name}</h3>
              <div className="text-xs text-muted-foreground mt-1 space-y-1">
                <div className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {e.date} {e.time && `· ${e.time}`}</div>
                <div className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {e.location}</div>
              </div>
              <span className="text-sm font-semibold text-primary mt-2 inline-block">Ver evento</span>
            </div>
          </Link>
        ))}
      </div>
    </SectionShell>
  );
};

/* ---------------- Tourist spots ---------------- */
export const TouristSpotsSection = () => {
  const { items } = useResource("tourist_spots");
  if (!items.length) return null;
  return (
    <SectionShell title="Pontos Turísticos" id="tourist_spots">
      <Carousel>
        {items.map((t) => (
          <Link key={t.id} to={`/ponto-turistico/${t.id}`} data-testid={`tourist-${t.id}`} className="snap-item flex-none w-[280px] relative rounded-2xl overflow-hidden h-52 group">
            <Img src={t.cover} alt={t.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 p-4 text-white">
              <h3 className="font-bold text-lg font-heading">{t.name}</h3>
              <p className="text-xs text-white/80 line-clamp-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> {t.location}</p>
              <span className="inline-block mt-1 text-xs font-semibold underline">Ver detalhes</span>
            </div>
          </Link>
        ))}
      </Carousel>
    </SectionShell>
  );
};

/* ---------------- Jobs ---------------- */
export const JobsSection = () => {
  const { items } = useResource("jobs");
  if (!items.length) return null;
  return (
    <SectionShell title="Vagas de Emprego" id="jobs">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {items.map((j) => (
          <div key={j.id} data-testid={`job-${j.id}`} className="bg-card rounded-2xl border border-border shadow-sm p-4">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center"><Briefcase className="w-5 h-5 text-primary" /></div>
              <div className="min-w-0">
                <h3 className="font-bold text-foreground leading-tight font-heading line-clamp-1">{j.title}</h3>
                <p className="text-xs text-muted-foreground line-clamp-1">{j.company} · {j.location}</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground line-clamp-2 mt-2">{j.description}</p>
            {j.requirements && <p className="text-xs text-foreground mt-1 line-clamp-1"><b>Requisitos:</b> {j.requirements}</p>}
            <Link to={`/vaga/${j.id}`} data-testid={`job-view-${j.id}`} className="mt-3 flex items-center justify-center gap-2 bg-secondary text-secondary-foreground text-sm font-semibold rounded-full py-2 active:scale-95 transition-transform">
              Ver vaga
            </Link>
          </div>
        ))}
      </div>
    </SectionShell>
  );
};

/* ---------------- Rentals ---------------- */
const PropertyCard = ({ p, testPrefix }) => (
  <div data-testid={`${testPrefix}-${p.id}`} className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden flex flex-col">
    <Img src={(p.photos && p.photos[0])} alt={p.title} className="w-full h-40 object-cover" />
    <div className="p-3 flex-1 flex flex-col">
      <h3 className="font-bold text-foreground leading-tight font-heading line-clamp-1">{p.title}</h3>
      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" /> {p.location}</p>
      <p className="text-xs text-muted-foreground line-clamp-2 mt-1 flex-1">{p.description}</p>
      <div className="flex items-center gap-3 mt-2 text-xs text-foreground">
        {p.bedrooms ? <span className="flex items-center gap-1"><BedDouble className="w-3.5 h-3.5" /> {p.bedrooms} quartos</span> : null}
        {p.size ? <span>{p.size}</span> : null}
      </div>
      <div className="text-lg font-bold text-primary font-heading mt-1">{formatBRL(p.price)}<span className="text-xs font-normal text-muted-foreground">/mês</span></div>
      {p.whatsapp && (
        <a href={waLink(p.whatsapp, `Olá! Vi o anúncio "${p.title}" no Divulga Pocinhos Guia e gostaria de mais informações.`)} target="_blank" rel="noreferrer" data-testid={`${testPrefix}-wa-${p.id}`} className="mt-3 flex items-center justify-center gap-2 bg-secondary text-secondary-foreground text-sm font-semibold rounded-full py-2 active:scale-95 transition-transform">
          <MessageCircle className="w-4 h-4" /> {p.contact || "Contato"}
        </a>
      )}
    </div>
  </div>
);

export const RentalsSection = () => {
  const { items } = useResource("rentals");
  if (!items.length) return null;
  return (
    <SectionShell title="Casas para Alugar" id="rentals">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {items.map((p) => <PropertyCard key={p.id} p={p} testPrefix="rental" />)}
      </div>
    </SectionShell>
  );
};

export const CommercialSpotsSection = () => {
  const { items } = useResource("commercial_spots");
  if (!items.length) return null;
  return (
    <SectionShell title="Pontos Comerciais" id="commercial_spots">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {items.map((p) => <PropertyCard key={p.id} p={p} testPrefix="commercial" />)}
      </div>
    </SectionShell>
  );
};

export const SECTION_COMPONENTS = {
  categories: CategoriesSection,
  banners: BannersSection,
  promotions: PromotionsSection,
  coupons: CouponsSection,
  featured_businesses: FeaturedBusinessesSection,
  all_businesses: AllBusinessesSection,
  news: NewsSection,
  events: EventsSection,
  tourist_spots: TouristSpotsSection,
  jobs: JobsSection,
  rentals: RentalsSection,
  commercial_spots: CommercialSpotsSection,
};
